// Shared job-costing + profitability math. Used by the Pricebook service form
// (live cost/profit preview) and the Reports → Job Profitability tab.

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

export function laborCost(svc) {
  if (!svc) return 0;
  if (svc.laborType === "hourly") return (Number(svc.laborRate) || 0) * (Number(svc.laborHours) || 0);
  if (svc.laborType === "flat") return Number(svc.laborFlat) || 0;
  return 0;
}

// Sum of BOM material costs × qty for a single unit of the service.
export function serviceMaterialCost(svc, byId) {
  let c = 0;
  (svc?.bom || []).forEach((b) => {
    const m = byId?.[b.pricebookId];
    if (m) c += (Number(m.cost) || 0) * (Number(b.qty) || 0);
  });
  return r2(c);
}

// A service's auto-computed internal cost = materials + labor.
export function serviceComputedCost(svc, byId) {
  return r2(serviceMaterialCost(svc, byId) + laborCost(svc));
}

export function profitOf(unitPrice, cost) {
  const up = Number(unitPrice) || 0;
  const p = up - (Number(cost) || 0);
  return { profit: r2(p), pct: up > 0 ? r1((p / up) * 100) : 0 };
}

// Hours between work start + complete (ISO timestamps) — null if not both present.
export function actualHoursFor(job) {
  if (!job?.workStartedAt || !job?.workCompletedAt) return null;
  const ms = new Date(job.workCompletedAt).getTime() - new Date(job.workStartedAt).getTime();
  if (!isFinite(ms) || ms <= 0) return null;
  return r2(ms / 3_600_000);
}

// Full profitability breakdown for one job against its source doc (invoice → estimate).
// byId: map of pricebookId → pricebook item.
export function computeJobProfit(job, doc, byId) {
  const materials = {}; // pricebookId → {name, qty, unitCost, total}
  let materialCost = 0;
  let expHours = 0;        // expected labor hours (hourly services)
  let expHourlyCost = 0;   // expected hourly labor $
  let flatCost = 0;        // flat labor $ (fixed)

  const addMaterial = (id, name, qty, unitCost) => {
    if (!materials[id]) materials[id] = { name, qty: 0, unitCost, total: 0 };
    materials[id].qty = r2(materials[id].qty + qty);
    materials[id].total = r2(materials[id].total + qty * unitCost);
  };

  (doc?.lineItems || []).forEach((li) => {
    const qty = Number(li.qty) || 0;
    const item = li.pricebookId ? byId?.[li.pricebookId] : null;
    if (item?.kind === "service") {
      (item.bom || []).forEach((b) => {
        const m = byId?.[b.pricebookId];
        if (!m) return;
        addMaterial(m.id, m.name, (Number(b.qty) || 0) * qty, Number(m.cost) || 0);
      });
      materialCost += serviceMaterialCost(item, byId) * qty;
      if (item.laborType === "hourly") {
        expHours += (Number(item.laborHours) || 0) * qty;
        expHourlyCost += (Number(item.laborRate) || 0) * (Number(item.laborHours) || 0) * qty;
      } else if (item.laborType === "flat") {
        flatCost += (Number(item.laborFlat) || 0) * qty;
      }
    } else if (item?.kind === "material") {
      addMaterial(item.id, item.name, qty, Number(item.cost) || 0);
      materialCost += (Number(item.cost) || 0) * qty;
    }
  });

  materialCost = r2(materialCost);
  expHourlyCost = r2(expHourlyCost);
  flatCost = r2(flatCost);

  const actualHours = actualHoursFor(job);
  const blendedRate = expHours > 0 ? expHourlyCost / expHours : 0;
  const actualHourlyCost = (actualHours != null && blendedRate)
    ? r2(actualHours * blendedRate)
    : expHourlyCost;

  const expectedCost = r2(materialCost + expHourlyCost + flatCost);
  const actualCost = r2(materialCost + actualHourlyCost + flatCost);
  const charged = r2(doc?.total || 0);

  const expectedProfit = r2(charged - expectedCost);
  const actualProfit = r2(charged - actualCost);
  const expectedMargin = charged > 0 ? r1((expectedProfit / charged) * 100) : 0;
  const actualMargin = charged > 0 ? r1((actualProfit / charged) * 100) : 0;

  return {
    materials: Object.values(materials),
    materialCost,
    expHours: r2(expHours),
    actualHours,
    blendedRate: r2(blendedRate),
    expHourlyCost,
    actualHourlyCost,
    flatCost,
    expectedCost,
    actualCost,
    charged,
    expectedProfit,
    actualProfit,
    expectedMargin,
    actualMargin,
    marginDelta: r1(actualMargin - expectedMargin),   // ≥0 on/above budget, <0 below
    costVariance: r2(actualCost - expectedCost),        // >0 over budget on labor
    hasData: charged > 0 || expectedCost > 0,
  };
}
